import { useRef, useState, useCallback, useEffect } from 'react';
import { pm2ConfigStore } from '@pm2/store';
import { breakpoints } from '@ui/css';
import { throttleToAnimationFrame } from '@utils/common';
import { useMatchBreakpoint, useForceHeaderExpanded, useScrollLocking, useForceHeaderCollapsed, useClickOutsideListener, CSSRefTransition } from '@utils/react';
import { useSearch } from '../hook.search.js';
import { Input } from './atom.input.js';
import { ResultList } from './molecule.result-list.js';
import { transitionCls, TRANSITION_DURATION_MS, formCls, searchbarCls, contentCls } from './root.quicksearch.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function Quicksearch({
  toggleRef,
  visible,
  onClose: onCloseCallback
}) {
  const baseUrl = pm2ConfigStore.useSelector(state => state.baseUrl);
  const searchPath = pm2ConfigStore.useSelector(state => state.config.searchPath);
  const formRef = useRef(null);
  const [query, setQuery] = useState("");
  const [selectedResultId, setSelectedResultId] = useState(undefined);
  const [isSearching, {
    searchResults,
    currentQuery
  }] = useSearch({
    query
  });
  const listExpanded = currentQuery.length > 0;
  const viewportOffset = useToggleViewportOffset(toggleRef, visible);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  useForceHeaderExpanded(typeof isMobile === "boolean" && !isMobile && visible);
  useScrollLocking(typeof isMobile === "boolean" && !isMobile && visible && currentQuery.length > 0);
  useForceHeaderCollapsed(typeof isMobile === "boolean" && isMobile && visible);
  const onClose = useCallback(resultClicked => {
    setQuery(() => {
      return "";
    });
    onCloseCallback();
  }, [onCloseCallback]);
  const onCloseWithoutResult = useCallback(() => {
    onClose(false);
  }, [onClose]);
  const onCloseAfterResultClick = useCallback(() => {
    onClose(true);
  }, [onClose]);
  const handleSelectionChange = useCallback(resultId => {
    setSelectedResultId(resultId);
  }, []);
  useClickOutsideListener(onCloseWithoutResult, formRef, {
    disabled: !visible,
    useCapturePhase: true
  });
  return /*#__PURE__*/jsx(CSSRefTransition, {
    in: visible,
    timeout: TRANSITION_DURATION_MS,
    classNames: transitionCls,
    mountOnEnter: true,
    unmountOnExit: true,
    nodeRef: formRef,
    children: ref => /*#__PURE__*/jsx("form", {
      ref: ref,
      className: formCls,
      role: "search",
      method: "GET",
      action: `${baseUrl}${searchPath}`,
      style: {
        // @ts-expect-error: error in React typings, css properties are
        // valid
        "--quicksearch-viewport-offset": `${viewportOffset}px`
      },
      children: /*#__PURE__*/jsxs("div", {
        className: searchbarCls
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        ,
        role: "combobox",
        "aria-owns": "quicksearch-listbox",
        "aria-haspopup": "listbox",
        "aria-expanded": listExpanded,
        children: [/*#__PURE__*/jsx("div", {
          className: contentCls,
          children: /*#__PURE__*/jsx(Input, {
            value: query,
            isLoading: isSearching,
            visible: visible,
            onClose: onCloseWithoutResult,
            onInput: setQuery,
            selectedResultId: selectedResultId
          })
        }), /*#__PURE__*/jsx(ResultList, {
          active: listExpanded,
          enteredQuery: query,
          currentQuery: currentQuery,
          results: searchResults,
          onClose: onCloseAfterResultClick,
          onSelectionChange: handleSelectionChange
        })]
      })
    })
  });
}
function useToggleViewportOffset(toggleRef, visible) {
  const [toggleOffset, setToggleOffset] = useState(0);
  useEffect(() => {
    if (!visible) {
      return;
    }
    const updateOffset = () => {
      if (!toggleRef.current) {
        return;
      }
      setToggleOffset(recursivelyGetOffset(toggleRef.current));
    };
    updateOffset();
    const handleResize = throttleToAnimationFrame(updateOffset);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [toggleRef, visible]);
  return toggleOffset;
}
function recursivelyGetOffset(elem) {
  let offset = 0;
  let currElem = elem;
  while (currElem) {
    offset += currElem.offsetLeft;
    currElem = currElem.offsetParent;
  }
  return offset;
}

export { Quicksearch };
//# sourceMappingURL=root.quicksearch.js.map
